/**
 * Flags a dataset as a Library collection, or clears the flag (#2460).
 *
 * A collection IS a dataset — `is_collection` is the only difference — so this
 * is dataset governance and lives beside the dataset's other governance
 * control rather than anywhere in the Library surfaces. The Library read side
 * (#2379) had no writer at all before this.
 *
 * Admin-curated, so the affordance is gated on both axes the endpoint
 * authorizes on: the `admin` role capability AND server-confirmed per-object
 * manage. Rendering it for one without the other would produce a 403 on click.
 */
import React, { useState } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import {
  CollectionsBookmark as CollectionIcon,
  CollectionsBookmarkOutlined as NotCollectionIcon,
} from "@mui/icons-material";

import { useSetDatasetCollectionFlag } from "src/api/collections";
import DeleteDialog from "src/reusable/components/DeleteDialog";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "src/helpers/apiError";

export interface CollectionFlagToggleProps {
  datasetId: number;
  datasetName?: string;
  /** Current state, from the dataset payload's `is_collection`. */
  isCollection: boolean;
  /** The `admin` role capability. */
  isAdmin: boolean;
  /**
   * Server-confirmed per-object manage on THIS dataset. Explicit-deny, matching
   * the rest of this header: only a present-and-false `my_access.can_manage`
   * denies, because a payload carrying no flags must not false-deny.
   */
  canManage: boolean;
  isMobile?: boolean;
}

const CollectionFlagToggle: React.FC<CollectionFlagToggleProps> = ({
  datasetId,
  datasetName,
  isCollection,
  isAdmin,
  canManage,
  isMobile = false,
}) => {
  const [confirmOff, setConfirmOff] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setFlag = useSetDatasetCollectionFlag();

  const apply = (next: boolean) => {
    setError(null);
    setFlag.mutate(
      { datasetId, isCollection: next },
      {
        onSuccess: () => {
          setConfirmOff(false);
          NucToast({
            message: next
              ? `"${datasetName ?? "This dataset"}" is now a Library collection.`
              : `"${datasetName ?? "This dataset"}" is no longer a Library collection.`,
            type: "success",
          });
        },
        // No optimistic flip: the control reflects the server's state, so a
        // rejected request leaves it where it was rather than moving and
        // silently moving back.
        onError: (err) => {
          const message = extractErrorDetail(
            err,
            "Couldn't change the collection flag.",
          );
          // Turning it off has a dialog to carry the message; turning it on
          // does not, so that failure has to toast or it is invisible.
          if (next) {
            NucToast({ message, type: "error" });
          } else {
            setError(message);
          }
        },
      },
    );
  };

  // The gate lives here rather than at the call site so a future host cannot
  // mount the control without it. Both axes, because the endpoint authorizes
  // on both and either alone 403s on click. Hidden, not disabled: a non-admin
  // has no path to this at all, so a disabled control would be permanently
  // dead rather than temporarily unavailable.
  if (!isAdmin || !canManage) return null;

  const label = isCollection ? "Remove from Library" : "Make a collection";
  const hint = isCollection
    ? "Stop showing this dataset as a Library collection"
    : "Show this dataset in the Library as a collection";
  const onClick = () => (isCollection ? setConfirmOff(true) : apply(true));
  const testId = `sdr-btn-collection-flag-${datasetId}`;

  return (
    <>
      <Tooltip title={hint}>
        <span>
          {isMobile ? (
            <IconButton
              size="small"
              onClick={onClick}
              disabled={setFlag.isLoading}
              aria-label={label}
              data-testid={testId}
              data-attr="datasets:btn:collection-flag"
            >
              {isCollection ? (
                <CollectionIcon fontSize="small" />
              ) : (
                <NotCollectionIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={onClick}
              disabled={setFlag.isLoading}
              startIcon={
                isCollection ? (
                  <CollectionIcon fontSize="small" />
                ) : (
                  <NotCollectionIcon fontSize="small" />
                )
              }
              data-testid={testId}
              data-attr="datasets:btn:collection-flag"
            >
              {label}
            </Button>
          )}
        </span>
      </Tooltip>

      {/* Turning it OFF needs the consequence stated: the dataset leaves every
          user's Library pane, and the pane's Collections group disappears
          outright for anyone who can read no others. Turning it ON is additive
          and reversible here, so it goes straight through. */}
      <DeleteDialog
        open={confirmOff}
        onClose={() => {
          setConfirmOff(false);
          setError(null);
        }}
        title={`Stop showing "${datasetName ?? "this dataset"}" in the Library?`}
        content={
          "It disappears from the Library for everyone who can see it, and the " +
          "Collections group vanishes for anyone with no other collection. " +
          "The dataset and its records are untouched, and any project " +
          "referencing a record keeps that reference."
        }
        onDelete={() => apply(false)}
        isLoading={setFlag.isLoading}
        isSuccess={setFlag.isSuccess}
        errorMessage={error}
        confirmButtonText="Remove from the Library"
        tid="collection-flag-off"
      />
    </>
  );
};

export default CollectionFlagToggle;
